import { useAccountAllowances } from "../hooks/useAccountAllowances";
import { bigDecimal } from "js-big-decimal";
import { createContext, useContext, useMemo } from "react";
import { useAuth } from "./connectionContext";
import { useAccountBalance } from "../hooks/useAccountBalance";
import strNumPretifier from "../utils/strNumPretifier";
import { BIG_DECIMAL_ZERO, BIG_DECIMAL_BILLION, MIN_NUMBER_TO_DISPLAY } from "../utils/constants";

type TBalanceContext = {
  balance: bigDecimal;
  allowances: bigDecimal;
  totalOST: bigDecimal;
  displayTotalOST: string;
  isLoading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
};

// Initial state
const initialState: Omit<TBalanceContext, "refetch"> = {
  balance: BIG_DECIMAL_ZERO,
  allowances: BIG_DECIMAL_ZERO,
  totalOST: BIG_DECIMAL_ZERO,
  displayTotalOST: "0",
  isLoading: false,
  error: null,
};

const BalanceContext = createContext<TBalanceContext>({
  ...initialState,
} as TBalanceContext);

export const BalanceProvider = ({ children }: { children: React.ReactNode }) => {
  const { account } = useAuth();
  const balanceQuery = useAccountBalance(account);
  const allowancesQuery = useAccountAllowances(account);
  const prettyBalance = useMemo(() => {
    if (!account || (!balanceQuery.data && !allowancesQuery.data)) return "0";
    const balance = balanceQuery.data?.divide(BIG_DECIMAL_BILLION) ?? BIG_DECIMAL_ZERO;
    const allowances = allowancesQuery.data?.divide(BIG_DECIMAL_BILLION) ?? BIG_DECIMAL_ZERO;
    const totalOSTBalances = balance.add(allowances).stripTrailingZero();
    if (totalOSTBalances.compareTo(BIG_DECIMAL_ZERO) === 0) return "0";
    if (totalOSTBalances.compareTo(MIN_NUMBER_TO_DISPLAY) <= 0)
      return `< ${MIN_NUMBER_TO_DISPLAY.getPrettyValue()}`;
    return strNumPretifier({ str: totalOSTBalances.getValue(), precision: 3 });
  }, [balanceQuery.data, allowancesQuery.data, account]);

  const valueProvider: TBalanceContext = {
    balance: balanceQuery.data?.divide(BIG_DECIMAL_BILLION).stripTrailingZero() ?? BIG_DECIMAL_ZERO,
    allowances:
      allowancesQuery.data?.divide(BIG_DECIMAL_BILLION).stripTrailingZero() ?? BIG_DECIMAL_ZERO,
    totalOST:
      balanceQuery.data
        ?.add(allowancesQuery.data ?? BIG_DECIMAL_ZERO)
        .stripTrailingZero()
        .divide(BIG_DECIMAL_BILLION)
        .stripTrailingZero() ?? BIG_DECIMAL_ZERO,
    displayTotalOST: prettyBalance,
    isLoading: balanceQuery.isLoading || allowancesQuery.isLoading,
    error: balanceQuery.error || allowancesQuery.error,
    refetch: async () => {
      await Promise.all([balanceQuery.refetch(), allowancesQuery.refetch()]);
    },
  };

  return <BalanceContext.Provider value={valueProvider}>{children}</BalanceContext.Provider>;
};

export const useAccountBalances = () => {
  const context = useContext(BalanceContext);
  if (context === undefined) {
    throw new Error("useAccountBalances must be used within an BalanceProvider");
  }
  return context;
};
